// import { ipcRenderer } from 'electron'
import isElectron from 'is-electron'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import Select from 'react-simpler-select'

import Button from './UI/Button'
import NumberInput from './UI/NumberInput'

import tooltips from '../constants/tooltips.json'

import { learnModePresetsMenu, learnModePresets } from "../constants/learnModePresets";

import {
  setSensitivityPreset,
  setPresetGain,
  setCrosstalkThreshold,
  setGlobalThreshold,
  selectPlayMode,
  setMIDINOteLength,
  setDampThreshold,
  openLearnMode,
  openVelocityCurves,
  openPreferences
} from '../actions/'

const playModeOptions = [
  { value: '0', label: 'Dampening' },
  { value: '1', label: 'Aftertouch' }
]

class Playability extends Component {
  componentDidMount() {
    if(isElectron()) ipcRenderer.on('preferences', (event, value) => this.props.onOpenPreferences())
  }

  componentWillUnmountMount() {
    if(isElectron()) ipcRenderer.off('preferences', (event, value) => this.props.onOpenPreferences())
  }

  render() {
    const {
      deviceConnected,
      playMode,
      noteOnTime,
      dampThreshold,
      onPlayModeChange,
      onNoteLengthChange,
      onDampThresholdChange,
      onOpenLearnMode,
      onOpenVelocityCurves,
      onOpenPreferences,
      onSetSensitivityPreset,
      onPresetGainChange,
      onSetGlobalThreshold,
      onSetCrosstalkThreshold,
      sensitivityPreset,
      showTooltips
    } = this.props

    return (
      <div className="playability">
        <div className="playability-grid">
          <h2
            data-tip={tooltips["editor-playability"]}
            data-multiline="true"
            data-place="top"
            data-tip-disable={!showTooltips}
          >
            Playability
          </h2>
          <div className="grid-item">
            <label
              data-tip={tooltips["editor-preset-select"]}
              data-multiline="true"
              data-place="right"
              data-tip-disable={!showTooltips}
            >
              Sensitivity Preset
              <Select
                name="sensitivitypresets"
                className={sensitivityPreset === '---' ? 'dirty' : ''}
                value={sensitivityPreset}
                options={learnModePresetsMenu}
                onChange={(id) => {
                  // update sensitivity preset
                  onSetSensitivityPreset(id);
                  // ignore changing values if dirty
                  if(id === '---') return
                  const { sensitivity, threshold, crosstalkThreshold } =
                    learnModePresets.find((p) => p.name === id);

                  // update values
                  onPresetGainChange(sensitivity);
                  onSetGlobalThreshold(threshold);
                  onSetCrosstalkThreshold(crosstalkThreshold);
                }}
              />
            </label>
          </div>

          <div className="grid-item">
            <label
              data-tip={tooltips["editor-play-mode"]}
              data-multiline="true"
              data-place="top"
              data-tip-disable={!showTooltips}
            >
              Play Mode
              <Select
                options={playModeOptions}
                onChange={onPlayModeChange}
                value={playMode}
              />
            </label>
          </div>

          <div className="grid-item">
            <label
              data-tip={tooltips["editor-note-duration"]}
              data-multiline="true"
              data-place="top"
              data-tip-disable={!showTooltips}
            >
              Note Duration
              <NumberInput
                name="midi-note-length"
                min={10}
                max={150}
                step={1}
                onChange={onNoteLengthChange}
                format={(value) => value + "ms"}
                value={noteOnTime}
              />
            </label>
          </div>

          <div className="grid-item">
            <label
              data-tip={tooltips["editor-damp-threshold"]}
              data-multiline="true"
              data-place="top"
              data-tip-disable={!showTooltips}
            >
              Damp Threshold
              <NumberInput
                name="damp-threshold"
                width={88}
                min={1}
                max={30}
                step={1}
                onChange={onDampThresholdChange}
                value={dampThreshold}
              />
            </label>
          </div>
        </div>
        <div className="globals-grid">
          <h2>Globals</h2>
          <div className="grid-item velocity-curves">
            <label
              data-tip={tooltips["editor-open-velocity-curves"]}
              data-multiline="true"
              data-place="top"
              data-tip-disable={!showTooltips}
            >
              Velocity Curves
              <Button name="Open" onClick={onOpenVelocityCurves} />
            </label>
          </div>
          <div className="grid-item bar-gain-mode">
            <label
              data-tip={
                tooltips[
                  !deviceConnected
                    ? "device-connect-message-bar-regulation"
                    : "editor-open-learn-mode"
                ]
              }
              data-multiline="true"
              data-place="top"
              data-type={!deviceConnected ? "warning" : "light"}
              data-tip-disable={!showTooltips}
            >
              Bar Regulation
              <Button
                name="Open"
                onClick={onOpenLearnMode}
                disabled={!deviceConnected}
              />
            </label>
          </div>
          <Button
            name="Preferences"
            onClick={onOpenPreferences}
            data-tip={tooltips["editor-open-preferences"]}
            data-multiline="true"
            data-place="right"
            data-tip-disable={!showTooltips}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    firmwareVersion: state.device.firmwareVersion,
    legacyFirmwareVersion: state.device.legacyFirmwareVersion,
    playMode: state.currentPreset.playParams.playMode,
    noteOnTime: state.currentPreset.playParams.noteOnTime,
    dampThreshold: state.currentPreset.playParams.dampThreshold,
    deviceConnected: state.device.connected,
    learnModeOpen: state.editor.learnModeOpen,
    sensitivityPreset: state.editor.sensitivityPreset,
    showTooltips: state.editorPreferences.showTooltips,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    onSetSensitivityPreset: (value) => {
      dispatch(setSensitivityPreset(value))
    },
    onPresetGainChange: (value) => {
      dispatch(setPresetGain(value));
    },
    onSetCrosstalkThreshold: (value) => {
      dispatch(setCrosstalkThreshold(value));
    },
    onSetGlobalThreshold: (value) => {
      dispatch(setGlobalThreshold(value));
    },
    onPlayModeChange: (value) => {
      dispatch(selectPlayMode(value))
    },
    onNoteLengthChange: (value) => {
      dispatch(setMIDINOteLength(value))
    },
    onDampThresholdChange: (value) => {
      dispatch(setDampThreshold(value))
    },
    onOpenLearnMode: () => {
      dispatch(openLearnMode())
    },
    onOpenVelocityCurves: () => {
      dispatch(openVelocityCurves())
    },
    onOpenPreferences: () => {
      dispatch(openPreferences(true))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Playability)
